import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuth, UserRole } from '@/contexts/AuthContext'

interface RequireRoleProps {
  children: ReactNode
  /**
   * Roles that are allowed to access this route.
   * If not specified, any authenticated user can access.
   */
  allowedRoles?: UserRole[]
  /**
   * Where to redirect if user doesn't have permission.
   * Defaults to '/' (dashboard)
   */
  redirectTo?: string
}

/**
 * Route protection component that checks if the user has the required role.
 * Must be used inside AuthProvider and after RequireAuth.
 *
 * Usage:
 * <RequireRole allowedRoles={['super_admin']}>
 *   <AdminOnlyComponent />
 * </RequireRole>
 */
export function RequireRole({
  children,
  allowedRoles,
  redirectTo = '/',
}: RequireRoleProps) {
  const { user, isAuthenticated, isLoading } = useAuth()
  const location = useLocation()

  // Still loading, show nothing
  if (isLoading) {
    return null
  }

  // Not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />
  }

  // No role restriction, allow access
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>
  }

  // Check if user has required role
  if (!allowedRoles.includes(user.role)) {
    return <Navigate to={redirectTo} replace />
  }

  return <>{children}</>
}

/**
 * Helper component for super_admin only routes
 */
export function RequireSuperAdmin({
  children,
  redirectTo = '/',
}: {
  children: ReactNode
  redirectTo?: string
}) {
  return (
    <RequireRole allowedRoles={['super_admin']} redirectTo={redirectTo}>
      {children}
    </RequireRole>
  )
}
